/**
 * Determines whether a KeyboardEvent matches a specific key combination.
 *
 * - `ctrl: true` with `fallbackToMeta: true` will match either Ctrl or Meta (Cmd) if user is on Mac.
 * - If `meta` or `ctrl` are explicitly defined, they are matched exactly.
 *
 * Note that for a combination to be detected on Mac, you must use `keydown` instead of `keyup` due
 * to a bug in Mac browsers where the keyup for the intended key (e.g., 'z') is not triggered when
 * a modifier key (Ctrl/Cmd) is held down.
 *
 * @param event - The keyboard event to match against.
 * @param combo - The key combination to check. Example: `{ key: 'z', ctrl: true }`
 * @param options - Optional flags. `{ fallbackToMeta: true }` enables Ctrl/Meta fallback.
 * @returns True if the event matches the given combination.
 */
export function matchesKey(
  event: KeyboardEvent,
  combo: { key: string; ctrl?: boolean; meta?: boolean },
  options: { fallbackToMeta?: boolean } = {},
): boolean {
  const key = combo.key.toLowerCase()
  const pressedKey = event.key.toLowerCase()
  const isMac = (navigator?.userAgentData?.platform || navigator?.platform || '').toUpperCase().indexOf('MAC') >= 0

  const keyPressed = pressedKey === key
  const metaMatch = combo.meta === undefined || event.metaKey === combo.meta
  let ctrlMatch = combo.ctrl === undefined || event.ctrlKey === combo.ctrl

  if (combo.ctrl) {
    // On Mac, some shortcuts use Meta (Cmd) instead of Ctrl
    if (isMac && options.fallbackToMeta) {
      ctrlMatch = event.metaKey === combo.ctrl
    }
  }
  return keyPressed && ctrlMatch && metaMatch
}
